import React, { useState } from 'react';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import Panel, { Stat } from '../../components/ui/Panel';
import { buttonClass } from '../../components/ui/Button';
import FadeIn from '../../components/ui/FadeIn';
import Appear from '../../components/ui/Appear';
import Reveal from '../../components/ui/Reveal';
import { Field } from '../../components/ui/Field';
import { CATEGORY } from '../../components/ui/categoryColors';

const FleetInventoryModule = ({ fleetCounts, onRefresh }) => {
  const api = useApi();
  const toast = useToast();

  const [editingInternal, setEditingInternal] = useState(false);
  const [internalLs, setInternalLs] = useState('');
  const [internalPyro, setInternalPyro] = useState('');
  const [editingSellable, setEditingSellable] = useState(false);
  const [sellableLs, setSellableLs] = useState('');
  const [sellablePyro, setSellablePyro] = useState('');
  const [editingSellableBatteries, setEditingSellableBatteries] = useState(false);
  const [sellableBatteryCount, setSellableBatteryCount] = useState('');

  const internalFleet = fleetCounts.internalFleet;
  const available = internalFleet != null ? internalFleet - (fleetCounts.totalLeased || 0) - (fleetCounts.totalRentalCommitted || 0) - (fleetCounts.activeFlagCount || 0) : null;
  const sellableAvailable = fleetCounts.sellableFleet != null ? fleetCounts.sellableFleet - fleetCounts.committedTotal : null;
  const sellableBatteriesAvailable = fleetCounts.sellableBatteries != null ? fleetCounts.sellableBatteries - fleetCounts.committedBatteries : null;

  const handleSaveInternal = async () => {
    try {
      await api.post('/dashboard/adjustments', {
        body: {
          adjustment_type: 'internal_fleet',
          ls_drone_count: parseInt(internalLs) || 0,
          pyro_drone_count: parseInt(internalPyro) || 0,
          battery_count: 0,
          flag_count: 0,
          notes: `Internal fleet set to ${parseInt(internalLs) || 0} Standard + ${parseInt(internalPyro) || 0} Pyro = ${(parseInt(internalLs) || 0) + (parseInt(internalPyro) || 0)}`,
          effective_date: new Date().toISOString().split('T')[0],
        },
      });
      toast.success('Internal fleet updated');
      setEditingInternal(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSaveSellable = async () => {
    try {
      await api.post('/dashboard/adjustments', {
        body: {
          adjustment_type: 'sellable_fleet',
          ls_drone_count: parseInt(sellableLs) || 0,
          pyro_drone_count: parseInt(sellablePyro) || 0,
          battery_count: 0,
          flag_count: 0,
          notes: `Sellable fleet set to ${parseInt(sellableLs) || 0} Standard + ${parseInt(sellablePyro) || 0} Pyro = ${(parseInt(sellableLs) || 0) + (parseInt(sellablePyro) || 0)}`,
          effective_date: new Date().toISOString().split('T')[0],
        },
      });
      toast.success('Sellable fleet updated');
      setEditingSellable(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSaveSellableBatteries = async () => {
    try {
      await api.post('/dashboard/adjustments', {
        body: {
          adjustment_type: 'sellable_batteries',
          ls_drone_count: 0,
          pyro_drone_count: 0,
          battery_count: parseInt(sellableBatteryCount) || 0,
          flag_count: 0,
          notes: `Sellable batteries set to ${parseInt(sellableBatteryCount) || 0}`,
          effective_date: new Date().toISOString().split('T')[0],
        },
      });
      toast.success('Sellable batteries updated');
      setEditingSellableBatteries(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Internal Fleet */}
      <Panel
        title="Internal Fleet"
        titleStyle="section"
        accent="primary"
        lit
        level={3}
        actions={(
          <button
            onClick={() => {
              if (!editingInternal) {
                setInternalLs(fleetCounts.internalLs || 0);
                setInternalPyro(fleetCounts.internalPyro || 0);
              }
              setEditingInternal(!editingInternal);
            }}
            className={buttonClass('quiet', 'sm')}
          >
            {editingInternal ? 'Cancel' : 'Edit'}
          </button>
        )}
      >
        <FadeIn key={String(editingInternal)}>
        {editingInternal ? (
          <div className="mt-3 space-y-2.5">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[10px] text-on-surface-variant uppercase tracking-wider">Standard</label>
                <Field type="number" value={internalLs} onChange={e => setInternalLs(e.target.value)} />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-on-surface-variant uppercase tracking-wider">Pyro</label>
                <Field type="number" value={internalPyro} onChange={e => setInternalPyro(e.target.value)} />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-on-surface-variant">Total: {(parseInt(internalLs) || 0) + (parseInt(internalPyro) || 0)}</span>
              <button onClick={handleSaveInternal}
                className={buttonClass('filled', 'sm')}>
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-4">
              <Stat
                label="Drones"
                value={internalFleet != null ? internalFleet : '\u2014'}
                tone="accent"
                size="lg"
              />
              {internalFleet != null && (
                <div className="flex items-baseline gap-3 pb-0.5 text-sm text-on-surface-variant">
                  <span>{fleetCounts.internalLs} <span className="text-xs">std</span></span>
                  <span>{fleetCounts.internalPyro} <span className="text-xs">pyro</span></span>
                </div>
              )}
            </div>
            {internalFleet != null && (fleetCounts.totalLeased > 0 || fleetCounts.activeFlagCount > 0) && (
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm mt-1.5">
                <Appear show={fleetCounts.totalLeased > 0}>
                  <span style={{ color: CATEGORY.blue.ink.oklch }}>{fleetCounts.totalLeased} leased</span>
                </Appear>
                <Appear show={fleetCounts.totalRentalCommitted > 0}>
                  <span style={{ color: CATEGORY.orange.ink.oklch }}>{fleetCounts.totalRentalCommitted} rented</span>
                </Appear>
              </div>
            )}
            <div className="text-xs text-on-surface-variant mt-1.5">
              {internalFleet != null ? 'Total operational fleet (shows, leases, rentals)' : 'Not set yet'}
            </div>
          </>
        )}
        </FadeIn>
      </Panel>

      {/* Sellable Fleet */}
      <Panel
        title="Sellable Fleet"
        titleStyle="section"
        accent="blue"
        lit
        level={3}
        actions={(
          <button
            onClick={() => {
              if (!editingSellable) {
                setSellableLs(fleetCounts.sellableLs || 0);
                setSellablePyro(fleetCounts.sellablePyro || 0);
              }
              setEditingSellable(!editingSellable);
            }}
            className={buttonClass('quiet', 'sm')}
          >
            {editingSellable ? 'Cancel' : 'Edit'}
          </button>
        )}
      >
        <FadeIn key={String(editingSellable)}>
        {editingSellable ? (
          <div className="mt-3 space-y-2.5">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[10px] text-on-surface-variant uppercase tracking-wider">Standard</label>
                <Field type="number" min="0" value={sellableLs} onChange={e => setSellableLs(e.target.value)} />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-on-surface-variant uppercase tracking-wider">Pyro</label>
                <Field type="number" min="0" value={sellablePyro} onChange={e => setSellablePyro(e.target.value)} />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-on-surface-variant">Total: {(parseInt(sellableLs) || 0) + (parseInt(sellablePyro) || 0)}</span>
              <button onClick={handleSaveSellable}
                className={buttonClass('filled', 'sm')}>
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-4">
              <Stat
                label="Drones"
                value={fleetCounts.sellableFleet != null ? fleetCounts.sellableFleet : '\u2014'}
                tone="blue"
                size="lg"
              />
              {fleetCounts.sellableFleet != null && (
                <div className="flex items-baseline gap-3 pb-0.5 text-sm text-on-surface-variant">
                  <span>{fleetCounts.sellableLs} <span className="text-xs">std</span></span>
                  <span>{fleetCounts.sellablePyro} <span className="text-xs">pyro</span></span>
                </div>
              )}
            </div>
            <Reveal show={fleetCounts.sellableFleet != null && fleetCounts.committedTotal > 0}>
              <div className="flex items-baseline gap-2 text-sm mt-1.5">
                <span style={{ color: CATEGORY.orange.ink.oklch }}>-{fleetCounts.committedTotal} committed</span>
                <span className="text-on-surface-disabled">=</span>
                <span className="font-bold" style={{ color: (sellableAvailable > 0 ? CATEGORY.green : CATEGORY.red).ink.oklch }}>
                  {sellableAvailable} available
                </span>
              </div>
            </Reveal>
            <div className="text-[11px] text-on-surface-variant mt-1.5">
              {fleetCounts.sellableFleet != null ? 'Available for sales' : 'Not set yet'}
            </div>
          </>
        )}
        </FadeIn>
      </Panel>

      {/* Sellable Batteries */}
      <Panel
        title="Sellable Batteries"
        titleStyle="section"
        accent="green"
        lit
        level={3}
        actions={(
          <button
            onClick={() => {
              if (!editingSellableBatteries) {
                setSellableBatteryCount(fleetCounts.sellableBatteries || 0);
              }
              setEditingSellableBatteries(!editingSellableBatteries);
            }}
            className={buttonClass('quiet', 'sm')}
          >
            {editingSellableBatteries ? 'Cancel' : 'Edit'}
          </button>
        )}
      >
        <FadeIn key={String(editingSellableBatteries)}>
        {editingSellableBatteries ? (
          <div className="mt-3 space-y-2.5">
            <div>
              <label className="text-[10px] text-on-surface-variant uppercase tracking-wider">Count</label>
              <Field type="number" min="0" value={sellableBatteryCount} onChange={e => setSellableBatteryCount(e.target.value)} />
            </div>
            <div className="flex justify-end">
              <button onClick={handleSaveSellableBatteries}
                className={buttonClass('filled', 'sm')}>
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <Stat
              label="Batteries"
              value={fleetCounts.sellableBatteries != null ? fleetCounts.sellableBatteries : '\u2014'}
              tone="green"
              size="lg"
            />
            <Reveal show={fleetCounts.sellableBatteries != null && fleetCounts.committedBatteries > 0}>
              <div className="flex items-baseline gap-2 text-sm mt-1.5">
                <span style={{ color: CATEGORY.orange.ink.oklch }}>-{fleetCounts.committedBatteries} committed</span>
                <span className="text-on-surface-disabled">=</span>
                <span className="font-bold" style={{ color: (sellableBatteriesAvailable > 0 ? CATEGORY.green : CATEGORY.red).ink.oklch }}>
                  {sellableBatteriesAvailable} available
                </span>
              </div>
            </Reveal>
            <div className="text-[11px] text-on-surface-variant mt-1.5">
              {fleetCounts.sellableBatteries != null ? 'Battery inventory for sales' : 'Not set yet'}
            </div>
          </>
        )}
        </FadeIn>
      </Panel>
    </div>
  );
};

export default FleetInventoryModule;
